import { useState } from 'react';
import { Mic, Paperclip, RotateCcw, X } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { AvatarCircle } from '@/components/avatar-circle';
import { openDocumentById } from '@/lib/documents/open-by-id';
import type { RendezVousMessage, RendezVousPerson } from '@/lib/tutor/rendezvous';

const TIME = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Dialogue du chat d'un rendez-vous passé (messages tapés + phrases
 * retranscrites en live), avec l'AVATAR de chaque expéditeur. Visible des DEUX
 * participants — ils ont tous deux pris part à ce chat. Le texte est affiché en
 * clair par React (échappé) : jamais d'HTML interprété.
 *
 * Un fichier partagé dans la visio (`source='file'`, migration 0262) s'affiche en
 * lien « 📎 nom » : un clic signe l'URL et l'ouvre ; supprimé depuis → « Fichier supprimé ».
 *
 * Messages MASQUÉS dans la visio (croix du chat, suppression douce) : ils
 * n'entrent pas dans le résumé. L'élève ne les voit pas ; le tuteur
 * (`canModerate`) les voit estompés pour les RÉTABLIR — et peut aussi masquer un
 * message d'ici avant de générer le résumé.
 */
export function RendezVousDialogue({
  messages,
  people,
  isLoading,
  myUserId,
  otherName,
  canModerate = false,
  onSetHidden,
  pending = false,
}: {
  messages: RendezVousMessage[];
  people: Record<string, RendezVousPerson>;
  isLoading: boolean;
  myUserId: string | undefined;
  otherName: string;
  canModerate?: boolean;
  onSetHidden?: (messageId: string, hidden: boolean) => void;
  pending?: boolean;
}) {
  const [fileError, setFileError] = useState<string | null>(null);
  const hiddenCount = messages.filter((m) => m.hiddenAt).length;
  const shown = canModerate ? messages : messages.filter((m) => !m.hiddenAt);
  const visibleCount = messages.length - hiddenCount;

  return (
    <section className="border-ink-100 rounded-2xl border bg-white p-4 text-left">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-ink-900 text-sm font-bold">Dialogue du chat</h2>
        {!isLoading && visibleCount > 0 && (
          <span className="text-ink-400 text-xs">
            {visibleCount} message{visibleCount > 1 ? 's' : ''}
            {canModerate &&
              hiddenCount > 0 &&
              ` · ${hiddenCount} masqué${hiddenCount > 1 ? 's' : ''}`}
          </span>
        )}
      </div>

      {isLoading ? (
        <p className="text-ink-500 mt-3 text-sm">Chargement du dialogue…</p>
      ) : shown.length === 0 ? (
        <p className="text-ink-500 mt-3 text-sm">Aucun message échangé pendant ce rendez-vous.</p>
      ) : (
        <ul className="mt-3 max-h-[60dvh] space-y-3 overflow-y-auto pr-1">
          {shown.map((m) => {
            const mine = m.senderId === myUserId;
            const person = people[m.senderId];
            const name = mine ? 'Toi' : person?.displayName || otherName;
            const hidden = !!m.hiddenAt;
            return (
              <li key={m.id} className="flex items-start gap-2">
                <AvatarCircle
                  name={person?.displayName || name}
                  src={person?.avatarUrl}
                  size="sm"
                  className={cn(hidden && 'opacity-50')}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 text-xs">
                    <span className="text-ink-700 font-semibold">{name}</span>
                    <span className="text-ink-400">{TIME.format(new Date(m.createdAt))}</span>
                    {m.source === 'voice' && (
                      <Mic
                        className="text-ink-400 size-3"
                        aria-label="Retranscrit automatiquement depuis la voix"
                      />
                    )}
                    {hidden && (
                      <span className="bg-ink-100 text-ink-500 rounded-full px-2 py-0.5 text-[11px] font-semibold">
                        Masqué — hors résumé
                      </span>
                    )}
                  </div>
                  {m.source === 'file' ? (
                    m.documentId ? (
                      <button
                        type="button"
                        onClick={() => {
                          setFileError(null);
                          openDocumentById(m.documentId as string).catch(() =>
                            setFileError(
                              "Impossible d'ouvrir ce fichier. Il a peut-être été supprimé.",
                            ),
                          );
                        }}
                        className={cn(
                          'mt-0.5 inline-flex min-h-[2.75rem] max-w-full items-center gap-1.5 text-left text-sm font-medium underline underline-offset-2 active:scale-[0.98]',
                          hidden ? 'text-ink-400 line-through' : 'text-primary-700',
                        )}
                      >
                        <Paperclip className="size-3.5 shrink-0" aria-hidden />
                        <span className="min-w-0 break-words">{m.body}</span>
                      </button>
                    ) : (
                      <p className="text-ink-400 mt-0.5 flex items-center gap-1.5 text-sm italic">
                        <Paperclip className="size-3.5 shrink-0" aria-hidden />
                        Fichier supprimé
                      </p>
                    )
                  ) : (
                    <p
                      className={cn(
                        'mt-0.5 whitespace-pre-wrap break-words text-sm',
                        hidden ? 'text-ink-400 line-through' : 'text-ink-800',
                      )}
                    >
                      {m.body}
                    </p>
                  )}
                </div>
                {canModerate && onSetHidden && (
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => onSetHidden(m.id, !hidden)}
                    aria-label={hidden ? 'Rétablir ce message' : 'Masquer ce message'}
                    title={hidden ? 'Rétablir ce message' : 'Masquer ce message'}
                    className="text-ink-400 hover:text-ink-700 -my-2 flex size-11 shrink-0 items-center justify-center transition-colors active:scale-90 disabled:opacity-50"
                  >
                    {hidden ? <RotateCcw className="size-3.5" /> : <X className="size-3.5" />}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {fileError && (
        <p role="alert" className="text-accent-700 mt-2 text-xs font-medium">
          {fileError}
        </p>
      )}
    </section>
  );
}
